import { format, formatDistanceToNow, isValid } from "date-fns";
import { toPlainText } from "@/lib/text";

export type NewsItem = {
  id: string;
  title: string;
  url: string;
  summary: string | null;
  image_url: string | null;
  published_at: string | null;
  fetched_at?: string | null;
  source: { name: string; url: string; logo_path: string | null } | null;
};

const parse = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? d : null;
};

const NewsCard = ({ item }: { item: NewsItem }) => {
  const published = parse(item.published_at);
  const fetched = parse(item.fetched_at);
  const title = toPlainText(item.title);
  const summary = toPlainText(item.summary);

  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-card rounded-2xl overflow-hidden shadow-card hover:shadow-bold transition-all duration-300 hover:-translate-y-0.5"
    >
      {item.image_url && (
        <div className="aspect-[16/9] overflow-hidden bg-muted">
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      )}
      <div className="p-5">
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <span className="font-semibold uppercase tracking-wider text-primary">
            {item.source?.name ?? "Unknown"}
          </span>
          {published && (
            <time dateTime={published.toISOString()} title={published.toLocaleString()}>
              {format(published, "d MMM yyyy")}
            </time>
          )}
        </div>
        <h3 className="font-display text-xl mt-2 leading-tight group-hover:text-primary transition-colors">
          {title}
        </h3>
        {summary && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{summary}</p>}
        <p className="mt-3 text-[11px] text-muted-foreground">
          {published ? `Published ${formatDistanceToNow(published, { addSuffix: true })}` : "Publication date unavailable"}
          {fetched && ` · Updated ${formatDistanceToNow(fetched, { addSuffix: true })}`}
        </p>
        <p className="mt-3 text-xs font-semibold inline-flex items-center gap-1 text-primary">
          Read on {item.source?.name}
          <span className="transition-transform group-hover:translate-x-1">→</span>
        </p>
      </div>
    </a>
  );
};

export default NewsCard;
